<template>
  <div class="k4-timeline-container">
    <!-- Timeline Header -->
    <div class="k4-timeline-header">
      <h5 class="mb-3">{{ $t('robotSchedulePlanning') }}</h5>
      <div class="k4-timeline-info">
        <span class="text-muted">{{ $t('timelineInstructions') }}</span>
      </div>
    </div>

    <!-- Timeline Scale -->
    <div class="k4-timeline-scale">
      <div class="k4-timeline-scale-label">{{ $t('timeScale') }}</div>
    </div>

    <!-- Timeline Content with integrated scale -->
    <div class="k4-timeline-content" ref="timelineContent">
      <!-- Timeline track -->
      <div class="k4-timeline-track" :style="{ width: totalWidth + 'px' }">
        <!-- Hour scale integrated -->
        <div class="k4-timeline-hours-integrated">
          <div 
            v-for="hour in 24" 
            :key="hour-1"
            class="k4-hour-marker-integrated"
            :style="{ left: (hour-1) * hourWidth + 'px', width: hourWidth + 'px' }"
          >
            <div class="k4-hour-label-integrated">{{ String(hour-1).padStart(2, '0') }}:00</div>
            <!-- Quarter hour markers -->
            <div class="k4-quarter-markers-integrated">
              <div class="k4-quarter-marker-integrated" style="left: 25%"></div>
              <div class="k4-quarter-marker-integrated" style="left: 50%"></div>
              <div class="k4-quarter-marker-integrated" style="left: 75%"></div>
            </div>
          </div>
        </div>

        <!-- Hour grid background -->
        <div class="k4-timeline-grid">
          <div 
            v-for="hour in 24" 
            :key="'grid-' + hour"
            class="k4-grid-column"
            :style="{ left: (hour-1) * hourWidth + 'px', width: hourWidth + 'px' }"
          ></div>
        </div>
        
        <!-- Loading overlay -->
        <div v-if="isLoading" class="k4-timeline-loading">
          <div class="spinner-border text-primary" role="status">
            <span class="sr-only">{{ $t('loading') }}...</span>
          </div>
          <p class="mt-2">{{ $t('loadingCheckpoints') }}</p>
        </div>
        
        <!-- Checkpoint blocks -->
        <div
          v-for="(checkpoint, index) in checkpoints"
          :key="'checkpoint-' + index"
          class="k4-checkpoint-block"
          :class="{ 
            'k4-dragging': draggedIndex === index,
            'k4-resizing': resizingIndex === index,
            'k4-invalid': !isCheckpointValid(checkpoint, index)
          }"
          :style="getCheckpointStyle(checkpoint)"
          @mousedown="startDrag($event, index)"
          @dblclick="editCheckpoint(index)"
          @mouseenter="showCheckpointTooltip($event, checkpoint)"
          @mouseleave="hideCheckpointTooltip"
        >
          <!-- Checkpoint content -->
          <div class="k4-checkpoint-content">
            <div class="k4-checkpoint-name">{{ getCheckpointName(checkpoint.checkpointId) }}</div>
            <div class="k4-checkpoint-direction">{{ checkpoint.direction === 'IN' ? $t('directionIN') : $t('directionOUT') }}</div>
            <div class="k4-checkpoint-time">
              {{ formatTime(checkpoint.startDate) }} - {{ formatTime(checkpoint.endDate) }}
            </div>
            <div class="k4-checkpoint-duration">{{ getDuration(checkpoint) }}</div>
          </div>

          <!-- Resize handles -->
          <div 
            class="k4-resize-handle k4-resize-left"
            @mousedown="startResize($event, index, 'left')"
          ></div>
          <div 
            class="k4-resize-handle k4-resize-right"
            @mousedown="startResize($event, index, 'right')"
          ></div>

          <!-- Delete button -->
          <div 
            class="k4-checkpoint-delete"
            @click="deleteCheckpoint(index)"
            v-if="!isReadOnly"
          >
            <font-awesome-icon :icon="['fas','times']" />
          </div>
        </div>

        <!-- Drag placeholder -->
        <div
          v-if="dragPlaceholder.visible"
          class="k4-drag-placeholder"
          :style="getDragPlaceholderStyle()"
        ></div>
      </div>
    </div>

    <!-- Add checkpoint section -->
    <div v-if="!isReadOnly && !isLoading && availableCheckpoints.length > 0" class="k4-add-checkpoint mt-4">
      <h6 class="k4-add-checkpoint-title mb-3">{{ $t('addCheckpoint') }}</h6>
      
      <!-- Search field -->
      <div class="row mb-3">
        <div class="col-lg-6">
          <div class="input-group">
            <input 
              id="checkpointSearchInput" 
              type="text" 
              v-model="searchQuery" 
              class="form-control" 
              :placeholder="$t('search')" 
              aria-label="Search" 
              aria-describedby="search-addon"
            >
            <div class="input-group-append">
              <span class="input-group-text" id="search-addon">
                <font-awesome-icon :icon="['fas','search']" size="1x" />
              </span>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Checkpoint cards -->
      <div v-if="availableCheckpointsForTimeline.length > 0" class="k4-checkpoint-cards-container">
        <div 
          v-for="checkpoint in availableCheckpointsForTimeline"
          :key="checkpoint.id"
          class="k4-checkpoint-card"
          @click="addCheckpointFromCard(checkpoint)"
        >
          <div class="k4-checkpoint-card-content">
            <div class="k4-checkpoint-card-name">{{ checkpoint.name }}</div>
            <div class="k4-checkpoint-card-button">
              <font-awesome-icon :icon="['fas','plus']" />
            </div>
          </div>
        </div>
      </div>
      
      <!-- No results message -->
      <div v-else-if="searchQuery && searchQuery.trim() !== ''" class="text-center text-muted py-4">
        <font-awesome-icon :icon="['fas','search']" class="mb-2" />
        <div>{{ $t('noSearchResults') }}</div>
      </div>
    </div>

    <!-- Validation messages -->
    <div v-if="validationErrors.length > 0" class="k4-validation-errors mt-3">
      <div class="alert alert-warning">
        <h6>{{ $t('validationErrors') }}:</h6>
        <ul class="mb-0">
          <li v-for="error in validationErrors" :key="error">{{ error }}</li>
        </ul>
      </div>
    </div>

    <!-- Resize tooltip -->
    <div 
      v-if="resizeTooltip.visible"
      class="k4-resize-tooltip"
      :style="{ left: resizeTooltip.x + 'px', top: resizeTooltip.y + 'px' }"
    >
      {{ resizeTooltip.text }}
    </div>

    <!-- Checkpoint hover tooltip -->
    <div 
      v-if="checkpointTooltip.visible"
      class="k4-checkpoint-tooltip"
      :style="{ left: checkpointTooltip.x + 'px', top: checkpointTooltip.y + 'px' }"
    >
      <div class="k4-tooltip-name">{{ checkpointTooltip.name }}</div>
      <div class="k4-tooltip-direction">{{ checkpointTooltip.direction }}</div>
      <div class="k4-tooltip-time">{{ checkpointTooltip.startTime }} ({{ checkpointTooltip.duration }})</div>
    </div>

    <!-- Edit Checkpoint Modal -->
    <BModal 
      v-model="editModal.show" 
      :title="$t('editCheckpoint')" 
      size="lg" 
      @ok="handleEditOk"
      :ok-disabled="isReadOnly"
      :ok-title="$t('save')"
      :cancel-title="$t('cancel')"
    >
      <div v-if="editModal.checkpoint" class="container-fluid">
        <div class="row pb-2">
          <div class="col-4">{{ $t('checkpoints') }}</div>
          <div class="col-8">
            <input 
              type="text" 
              class="form-control" 
              :value="getCheckpointName(editModal.checkpoint.checkpointId)"
              disabled
              readonly
            />
          </div>
        </div>
        <div class="row pb-2">
          <div class="col-4">{{ $t('direction') }}</div>
          <div class="col-8">
            <select v-model="editModal.checkpoint.direction" class="form-control" :disabled="isReadOnly">
              <option value="IN">{{ $t("directionIN") }}</option>
              <option value="OUT">{{ $t("directionOUT") }}</option>
            </select>
          </div>
        </div>
        <div class="row pb-2">
          <div class="col-4">{{ $t('start') }}</div>
          <div class="col-8">
            <VueDatePicker 
              v-model="editModal.startTime" 
              time-picker
              :minutes-increment="5" 
              :disabled="isReadOnly"
              :clearable="false"
              :text-input="true"
              @text-submit="validateTimeInput('start')"
              @closed="validateTimeInput('start')"
              teleport="body"
            />
          </div>
        </div>
        <div class="row pb-2">
          <div class="col-4">{{ $t('end') }}</div>
          <div class="col-8">
            <VueDatePicker 
              v-model="editModal.endTime" 
              time-picker
              :minutes-increment="5" 
              :disabled="isReadOnly"
              :clearable="false"
              :text-input="true"
              @text-submit="validateTimeInput('end')"
              @closed="validateTimeInput('end')"
              teleport="body"
            />
          </div>
        </div>
      </div>
    </BModal>
  </div>
</template>

<script>
import { DateTime } from "luxon";
import RobotCheckpointModel from '../../models/robotCheckpointModel';

export default {
  name: 'RobotScheduleTimeline',
  props: {
    value: {
      type: Array,
      default: () => []
    },
    availableCheckpoints: {
      type: Array,
      default: () => []
    },
    dateOfWork: {
      type: [String, Date],
      required: true
    },
    evt: {
      type: Object,
      required: true
    },
    isReadOnly: {
      type: Boolean,
      default: false
    },
    isLoading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      checkpoints: [],
      hourWidth: 80, // Increased width for better readability
      draggedIndex: -1,
      resizingIndex: -1,
      resizeDirection: null,
      dragOffset: { x: 0, y: 0 },
      isDragging: false,
      dragPlaceholder: {
        visible: false,
        x: 0,
        width: 0
      },
      newCheckpoint: {
        checkpointId: 0,
        direction: 'IN',
        startTime: '08:00:00',
        endTime: '08:30:00'
      },
      validationErrors: [],
      resizeTooltip: {
        visible: false,
        x: 0,
        y: 0,
        text: ''
      },
      checkpointTooltip: {
        visible: false,
        x: 0,
        y: 0,
        name: '',
        direction: '',
        startTime: '',
        duration: ''
      },
      editModal: {
        show: false,
        checkpoint: null,
        index: -1,
        startTime: { hours: 8, minutes: 0, seconds: 0 },
        endTime: { hours: 8, minutes: 30, seconds: 0 }
      },
      searchQuery: ''
    };
  },
  computed: {
    totalWidth() {
      return 24 * this.hourWidth;
    },
    availableCheckpointsForTimeline() {
      const usedCheckpointIds = this.checkpoints.map(checkpoint => checkpoint.checkpointId);
      let filteredCheckpoints = this.availableCheckpoints.filter(checkpoint => !usedCheckpointIds.includes(checkpoint.id));
      
      // Appliquer le filtre de recherche si une requête est présente
      if (this.searchQuery && this.searchQuery.trim() !== '') {
        filteredCheckpoints = filteredCheckpoints.filter(checkpoint => 
          checkpoint.name.toLowerCase().includes(this.searchQuery.toLowerCase())
        );
      }
      
      return filteredCheckpoints;
    }
  },
  watch: {
    value: {
      handler(newValue) {
        // Éviter la réinitialisation si nous sommes en train de faire un drag ou resize
        if (this.isDragging || this.resizingIndex >= 0) {
          return;
        }
        
        // Toujours mettre à jour si la longueur change ou si c'est un tableau vide
        const shouldUpdate = !newValue || newValue.length === 0 || 
                            this.checkpoints.length !== newValue.length ||
                            JSON.stringify(this.checkpoints) !== JSON.stringify(newValue);
        
        if (shouldUpdate) {
          // Convertir toutes les dates en objets DateTime pour une utilisation cohérente
          this.checkpoints = newValue.map(cp => ({
            ...cp,
            startDate: (typeof cp.startDate === 'object' && cp.startDate.toFormat)
              ? cp.startDate 
              : DateTime.fromISO(cp.startDate, { zone: this.evt.timezoneIso }),
            endDate: (typeof cp.endDate === 'object' && cp.endDate.toFormat)
              ? cp.endDate 
              : DateTime.fromISO(cp.endDate, { zone: this.evt.timezoneIso })
          }));
          this.validateSchedule();
        }
      },
      immediate: true,
      deep: true
    },
    checkpoints: {
      handler(newValue) {
        // Éviter l'émission si nous sommes déjà en train de synchroniser
        if (this.isDragging || this.resizingIndex >= 0) {
          return;
        }
        
        this.$emit('input', newValue);
        this.validateSchedule();
      },
      deep: true
    }
  },
  mounted() {
    document.addEventListener('mousemove', this.handleMouseMove);
    document.addEventListener('mouseup', this.handleMouseUp);
  },
  beforeDestroy() {
    document.removeEventListener('mousemove', this.handleMouseMove);
    document.removeEventListener('mouseup', this.handleMouseUp);
  },
  methods: {
    handleEditOk(bvModalEvent) {
      if (!this.isReadOnly) {
        bvModalEvent.preventDefault();
        this.saveCheckpointEdit();
      }
    },
    validateTimeInput(field) {
      // Validate and round minutes to nearest multiple of 5
      const timeObj = field === 'start' ? this.editModal.startTime : this.editModal.endTime;
      
      if (timeObj && typeof timeObj === 'object' && timeObj.minutes !== undefined) {
        // Round minutes to nearest multiple of 5
        const roundedMinutes = Math.round(timeObj.minutes / 5) * 5;
        
        // Handle overflow (60 minutes)
        if (roundedMinutes === 60) {
          timeObj.minutes = 0;
          timeObj.hours = (timeObj.hours + 1) % 24;
        } else {
          timeObj.minutes = roundedMinutes;
        }
        
        // Update the model to trigger reactivity
        if (field === 'start') {
          this.editModal.startTime = { ...timeObj };
        } else {
          this.editModal.endTime = { ...timeObj };
        }
      }
    },
    getCheckpointStyle(checkpoint) {
      const startMinutes = this.getMinutesFromMidnight(checkpoint.startDate);
      const duration = this.getDurationInMinutes(checkpoint);
      const left = (startMinutes / 60) * this.hourWidth;
      const width = (duration / 60) * this.hourWidth;
      
      return {
        left: left + 'px',
        width: width + 'px'
      };
    },
    
    getMinutesFromMidnight(dateTime) {
      return dateTime.hour * 60 + dateTime.minute;
    },
    
    getDurationInMinutes(checkpoint) {
      // Vérifier que startDate et endDate sont des objets DateTime valides
      if (!checkpoint.startDate || !checkpoint.endDate) {
        return 0;
      }
      
      // S'assurer que ce sont des objets DateTime valides
      // Luxon DateTime objects have a 'diff' method and 'isValid' property
      const startDate = (typeof checkpoint.startDate === 'object' && checkpoint.startDate.diff && checkpoint.startDate.isValid !== undefined)
        ? checkpoint.startDate 
        : DateTime.fromISO(checkpoint.startDate, { zone: this.evt.timezoneIso });
      
      const endDate = (typeof checkpoint.endDate === 'object' && checkpoint.endDate.diff && checkpoint.endDate.isValid !== undefined)
        ? checkpoint.endDate 
        : DateTime.fromISO(checkpoint.endDate, { zone: this.evt.timezoneIso });
      
      // Verify both dates are valid
      if (!startDate.isValid || !endDate.isValid) {
        return 0;
      }
      
      const diff = endDate.diff(startDate, 'minutes').minutes;
      
      if (isNaN(diff)) {
        return 0;
      }
      
      return diff;
    },
    
    getDuration(checkpoint) {
      const duration = this.getDurationInMinutes(checkpoint);
      const hours = Math.floor(duration / 60);
      const minutes = duration % 60;
      if (hours > 0) {
        return `${hours}h${minutes > 0 ? minutes.toString().padStart(2, '0') : ''}`;
      }
      return `${minutes}min`;
    },
    
    formatTime(dateTime) {
      return dateTime.toFormat("HH:mm");
    },
    
    getCheckpointName(checkpointId) {
      const checkpoint = this.availableCheckpoints.find(c => c.id === checkpointId);
      return checkpoint ? checkpoint.name : '';
    },
    
    startDrag(evt, index) {
      if (this.isReadOnly) return;
      
      evt.preventDefault();
      this.draggedIndex = index;
      this.isDragging = true;
      
      // Cacher le tooltip de checkpoint pendant le drag
      this.hideCheckpointTooltip();
      
      const rect = evt.currentTarget.getBoundingClientRect();
      
      // Calculate offset relative to the checkpoint block
      this.dragOffset.x = evt.clientX - rect.left;
      this.dragOffset.y = evt.clientY - rect.top;
    },
    
    startResize(evt, index, direction) {
      if (this.isReadOnly) return;
      
      evt.preventDefault();
      evt.stopPropagation();
      
      this.resizingIndex = index;
      this.resizeDirection = direction;
      
      // Cacher le tooltip de checkpoint pendant le resize
      this.hideCheckpointTooltip();
      
      this.showResizeTooltip(evt);
    },
    
    handleMouseMove(evt) {
      if (this.isDragging && this.draggedIndex >= 0) {
        this.handleDragMove(evt);
      } else if (this.resizingIndex >= 0) {
        this.handleResizeMove(evt);
      }
    },
    
    handleDragMove(evt) {
      const rect = this.$refs.timelineContent.getBoundingClientRect();
      const scrollLeft = this.$refs.timelineContent.scrollLeft;
      const x = evt.clientX - rect.left + scrollLeft - this.dragOffset.x;
      
      // Auto-scroll when dragging near edges
      const scrollMargin = 50;
      const scrollSpeed = 10;
      
      if (evt.clientX - rect.left < scrollMargin && scrollLeft > 0) {
        // Scroll left
        this.$refs.timelineContent.scrollLeft = Math.max(0, scrollLeft - scrollSpeed);
      } else if (evt.clientX - rect.left > rect.width - scrollMargin) {
        // Scroll right
        const maxScroll = this.totalWidth - rect.width;
        this.$refs.timelineContent.scrollLeft = Math.min(maxScroll, scrollLeft + scrollSpeed);
      }
      
      // Show drag placeholder
      this.dragPlaceholder.visible = true;
      this.dragPlaceholder.x = Math.max(0, Math.min(x, this.totalWidth - 100));
      
      const checkpoint = this.checkpoints[this.draggedIndex];
      this.dragPlaceholder.width = this.getDurationInMinutes(checkpoint) / 60 * this.hourWidth;
    },
    
    handleResizeMove(evt) {
      const rect = this.$refs.timelineContent.getBoundingClientRect();
      const scrollLeft = this.$refs.timelineContent.scrollLeft;
      const x = evt.clientX - rect.left + scrollLeft;
      const checkpoint = this.checkpoints[this.resizingIndex];
      
      // Snap to 5-minute intervals
      const totalMinutes = Math.round((x / this.hourWidth) * 60 / 5) * 5;
      
      // Parse dateOfWork whether it's a string or Date object
      let dateStr;
      if (typeof this.dateOfWork === 'string') {
        dateStr = this.dateOfWork;
      } else if (this.dateOfWork instanceof Date) {
        dateStr = this.dateOfWork.toISOString().split('T')[0];
      } else {
        return;
      }
      
      const dateParts = dateStr.split('-');
      const newDateTime = DateTime.fromObject({
        year: parseInt(dateParts[0]),
        month: parseInt(dateParts[1]),
        day: parseInt(dateParts[2]),
        hour: Math.floor(totalMinutes / 60),
        minute: totalMinutes % 60
      }, { zone: this.evt.timezoneIso });
      
      if (this.resizeDirection === 'left') {
        // Resize start time
        if (newDateTime < checkpoint.endDate.minus({ minutes: 5 })) {
          checkpoint.startDate = newDateTime;
        }
      } else {
        // Resize end time
        if (newDateTime > checkpoint.startDate.plus({ minutes: 5 })) {
          checkpoint.endDate = newDateTime;
        }
      }
      
      this.updateResizeTooltip(evt, checkpoint);
    },
    
    handleMouseUp(evt) {
      if (this.isDragging && this.draggedIndex >= 0) {
        this.completeDrag(evt);
      } else if (this.resizingIndex >= 0) {
        this.completeResize();
      }
    },
    
    completeDrag(evt) {
      const rect = this.$refs.timelineContent.getBoundingClientRect();
      const scrollLeft = this.$refs.timelineContent.scrollLeft;
      const x = evt.clientX - rect.left + scrollLeft - this.dragOffset.x;
      
      // Calculate new start time based on position
      const totalMinutes = Math.round((x / this.hourWidth) * 60 / 5) * 5;
      
      // Parse dateOfWork whether it's a string or Date object
      let dateStr;
      if (typeof this.dateOfWork === 'string') {
        dateStr = this.dateOfWork;
      } else if (this.dateOfWork instanceof Date) {
        dateStr = this.dateOfWork.toISOString().split('T')[0];
      } else {
        return;
      }
      
      const dateParts = dateStr.split('-');
      const newStartTime = DateTime.fromObject({
        year: parseInt(dateParts[0]),
        month: parseInt(dateParts[1]),
        day: parseInt(dateParts[2]),
        hour: Math.floor(totalMinutes / 60),
        minute: totalMinutes % 60
      }, { zone: this.evt.timezoneIso });
      
      // Update checkpoint position - KEEP user-defined start time
      const checkpoint = this.checkpoints[this.draggedIndex];
      const duration = this.getDurationInMinutes(checkpoint);
      checkpoint.startDate = newStartTime;
      checkpoint.endDate = newStartTime.plus({ minutes: duration });
      
      // Recalculate other checkpoints based on new rules
      this.recalculateAfterDragDrop(this.draggedIndex);
      
      // Forcer l'émission vers le parent pour s'assurer que les changements sont persistés
      this.$emit('input', [...this.checkpoints]);
      
      // Reset drag state
      this.draggedIndex = -1;
      this.isDragging = false;
      this.dragPlaceholder.visible = false;
    },
    
    completeResize() {
      // Recalculate subsequent checkpoints to maintain continuity
      this.recalculateSchedule();
      
      // Forcer l'émission vers le parent pour s'assurer que les changements sont persistés
      this.$emit('input', [...this.checkpoints]);
      
      this.resizingIndex = -1;
      this.resizeDirection = null;
      this.resizeTooltip.visible = false;
    },
    
    getDragPlaceholderStyle() {
      return {
        left: this.dragPlaceholder.x + 'px',
        width: this.dragPlaceholder.width + 'px'
      };
    },
    
    showResizeTooltip(evt) {
      this.resizeTooltip.visible = true;
      this.resizeTooltip.x = evt.clientX + 10;
      this.resizeTooltip.y = evt.clientY - 10;
    },
    
    updateResizeTooltip(evt, checkpoint) {
      this.resizeTooltip.x = evt.clientX + 10;
      this.resizeTooltip.y = evt.clientY - 10;
      this.resizeTooltip.text = this.getDuration(checkpoint);
    },
    
    showCheckpointTooltip(evt, checkpoint) {
      // Ne pas afficher le tooltip si on est en train de drag ou resize
      if (this.isDragging || this.resizingIndex >= 0) return;
      
      this.checkpointTooltip.visible = true;
      this.checkpointTooltip.x = evt.clientX + 10;
      this.checkpointTooltip.y = evt.clientY - 60; // Plus haut pour éviter de masquer le checkpoint
      this.checkpointTooltip.name = this.getCheckpointName(checkpoint.checkpointId);
      this.checkpointTooltip.direction = checkpoint.direction === 'IN' ? this.$t('directionIN') : this.$t('directionOUT');
      this.checkpointTooltip.startTime = this.formatTime(checkpoint.startDate);
      this.checkpointTooltip.duration = this.getDuration(checkpoint);
    },
    
    hideCheckpointTooltip() {
      this.checkpointTooltip.visible = false;
    },
    
    addCheckpoint() {
      if (this.newCheckpoint.checkpointId === 0) {
        this.$emit('validation-error', this.$t('pleaseSelectCheckpoint'));
        return;
      }
      
      let startDateTime, endDateTime;
      
      if (this.checkpoints.length === 0) {
        // First checkpoint - use the specified times
        startDateTime = DateTime.fromISO(
          this.dateOfWork + "T" + this.newCheckpoint.startTime, 
          { zone: this.evt.timezoneIso }
        );
        endDateTime = DateTime.fromISO(
          this.dateOfWork + "T" + this.newCheckpoint.endTime, 
          { zone: this.evt.timezoneIso }
        );
      } else {
        // Subsequent checkpoints - add after the last one to maintain continuity
        const sortedCheckpoints = [...this.checkpoints].sort((a, b) => 
          a.startDate.toMillis() - b.startDate.toMillis()
        );
        const lastCheckpoint = sortedCheckpoints[sortedCheckpoints.length - 1];
        
        startDateTime = lastCheckpoint.endDate;
        const requestedDuration = DateTime.fromISO(
          this.dateOfWork + "T" + this.newCheckpoint.endTime
        ).diff(
          DateTime.fromISO(this.dateOfWork + "T" + this.newCheckpoint.startTime), 
          'minutes'
        ).minutes;
        
        endDateTime = startDateTime.plus({ minutes: Math.max(5, requestedDuration) });
      }
      
      if (startDateTime >= endDateTime) {
        this.$emit('validation-error', this.$t('invalidTimeRange'));
        return;
      }
      
      // Check if checkpoint already exists
      const exists = this.checkpoints.some(cp => 
        cp.checkpointId === this.newCheckpoint.checkpointId && 
        cp.direction === this.newCheckpoint.direction
      );
      
      if (exists) {
        this.$emit('validation-error', this.$t('checkpointUniquenessError'));
        return;
      }
      
      const newCheckpoint = new RobotCheckpointModel(
        this.newCheckpoint.checkpointId,
        this.newCheckpoint.direction,
        startDateTime,
        endDateTime
      );
      
      this.checkpoints.push(newCheckpoint);
      
      // Reset form
      this.newCheckpoint.checkpointId = 0;
      this.newCheckpoint.startTime = '08:00:00';
      this.newCheckpoint.endTime = '09:00:00';
    },
    
    addCheckpointFromCard(checkpoint) {
      // Utiliser les valeurs par défaut pour créer un nouveau checkpoint
      this.newCheckpoint.checkpointId = checkpoint.id;
      this.newCheckpoint.direction = 'IN'; // Direction par défaut
      this.newCheckpoint.startTime = '08:00:00'; // Temps par défaut
      this.newCheckpoint.endTime = '08:30:00'; // Temps par défaut
      
      // Appeler la méthode addCheckpoint existante
      this.addCheckpoint();
    },
    
    deleteCheckpoint(index) {
      if (this.isReadOnly) return;
      
      // Supprimer le checkpoint de la liste locale
      this.checkpoints.splice(index, 1);
      
      // Forcer l'émission immédiate vers le parent pour éviter la restauration lors du prochain drag & drop
      this.$emit('input', [...this.checkpoints]);
      
      // Recalculer la planification pour maintenir la continuité
      this.recalculateSchedule();
    },
    
    editCheckpoint(index) {
      if (this.isReadOnly) return;
      
      const checkpoint = this.checkpoints[index];
      this.editModal.checkpoint = { ...checkpoint };
      this.editModal.index = index;
      
      // S'assurer que les dates sont des objets DateTime valides
      const startDate = (typeof checkpoint.startDate === 'object' && checkpoint.startDate.toFormat)
        ? checkpoint.startDate 
        : DateTime.fromISO(checkpoint.startDate, { zone: this.evt.timezoneIso });
      
      const endDate = (typeof checkpoint.endDate === 'object' && checkpoint.endDate.toFormat)
        ? checkpoint.endDate 
        : DateTime.fromISO(checkpoint.endDate, { zone: this.evt.timezoneIso });
      
      // VueDatePicker en mode time-picker attend un objet { hours, minutes, seconds }
      this.editModal.startTime = {
        hours: startDate.hour,
        minutes: startDate.minute,
        seconds: startDate.second
      };
      this.editModal.endTime = {
        hours: endDate.hour,
        minutes: endDate.minute,
        seconds: endDate.second
      };
      
      this.editModal.show = true;
    },
    
    saveCheckpointEdit() {
      const checkpoint = this.editModal.checkpoint;
      const index = this.editModal.index;
      
      // Convertir les objets { hours, minutes, seconds } en format HH:mm:ss
      const startTimeStr = typeof this.editModal.startTime === 'object'
        ? `${String(this.editModal.startTime.hours).padStart(2, '0')}:${String(this.editModal.startTime.minutes).padStart(2, '0')}:${String(this.editModal.startTime.seconds || 0).padStart(2, '0')}`
        : this.editModal.startTime;
      
      const endTimeStr = typeof this.editModal.endTime === 'object'
        ? `${String(this.editModal.endTime.hours).padStart(2, '0')}:${String(this.editModal.endTime.minutes).padStart(2, '0')}:${String(this.editModal.endTime.seconds || 0).padStart(2, '0')}`
        : this.editModal.endTime;
      
      // Create new DateTime objects
      const startDateTime = DateTime.fromISO(
        this.dateOfWork + "T" + startTimeStr, 
        { zone: this.evt.timezoneIso }
      );
      const endDateTime = DateTime.fromISO(
        this.dateOfWork + "T" + endTimeStr, 
        { zone: this.evt.timezoneIso }
      );
      
      // Validate
      if (startDateTime >= endDateTime) {
        this.$emit('validation-error', this.$t('invalidTimeRange'));
        return;
      }
      
      if (endDateTime.diff(startDateTime, 'minutes').minutes < 5) {
        this.$emit('validation-error', this.$t('minimumDurationError'));
        return;
      }
      
      // Update the checkpoint (checkpointId is no longer editable)
      this.checkpoints[index].direction = checkpoint.direction;
      this.checkpoints[index].startDate = startDateTime;
      this.checkpoints[index].endDate = endDateTime;
      
      // Recalculate schedule to maintain continuity
      this.recalculateSchedule();
      
      this.editModal.show = false;
    },
    
    isCheckpointValid(checkpoint, index) {
      // Check minimum duration (5 minutes)
      const duration = this.getDurationInMinutes(checkpoint);
      if (duration < 5) return false;
      
      // Check overlaps with other checkpoints
      for (let i = 0; i < this.checkpoints.length; i++) {
        if (i === index) continue;
        const other = this.checkpoints[i];
        if (this.checkpointsOverlap(checkpoint, other)) {
          return false;
        }
      }
      
      return true;
    },
    
    checkpointsOverlap(a, b) {
      // Two intervals overlap if one starts before the other ends AND vice versa
      // BUT we exclude the case where one ends exactly when the other starts (contiguous intervals)
      return a.startDate.toMillis() < b.endDate.toMillis() && a.endDate.toMillis() > b.startDate.toMillis();
    },
    
    validateSchedule() {
      this.validationErrors = [];
      
      // Check for overlaps
      for (let i = 0; i < this.checkpoints.length; i++) {
        for (let j = i + 1; j < this.checkpoints.length; j++) {
          if (this.checkpointsOverlap(this.checkpoints[i], this.checkpoints[j])) {
            this.validationErrors.push(this.$t('checkpointOverlapError'));
            break;
          }
        }
      }
      
      // Check continuity
      if (this.checkpoints.length > 1) {
        const sorted = [...this.checkpoints].sort((a, b) => 
          a.startDate.toMillis() - b.startDate.toMillis()
        );
        
        for (let i = 1; i < sorted.length; i++) {
          if (sorted[i].startDate.toMillis() !== sorted[i-1].endDate.toMillis()) {
            this.validationErrors.push(this.$t('checkpointContinuityError'));
            break;
          }
        }
      }
      
      // Check minimum duration
      for (const checkpoint of this.checkpoints) {
        if (this.getDurationInMinutes(checkpoint) < 5) {
          this.validationErrors.push(this.$t('minimumDurationError'));
          break;
        }
      }
      
      // Check uniqueness (same checkpoint + direction)
      const seen = new Set();
      for (const checkpoint of this.checkpoints) {
        const key = `${checkpoint.checkpointId}-${checkpoint.direction}`;
        if (seen.has(key)) {
          this.validationErrors.push(this.$t('checkpointUniquenessError'));
          break;
        }
        seen.add(key);
      }
    },
    
    recalculateAfterDragDrop(originalDraggedIndex) {
      if (this.checkpoints.length <= 1) return;
      
      // Store reference to the dragged checkpoint before sorting
      const draggedCheckpoint = this.checkpoints[originalDraggedIndex];
      
      // Sort all checkpoints by start time to get the new chronological order
      this.checkpoints.sort((a, b) => a.startDate.toMillis() - b.startDate.toMillis());
      
      // Find the new position of the dragged checkpoint after sorting
      const newDraggedIndex = this.checkpoints.findIndex(cp => cp === draggedCheckpoint);
      
      // Recalculate positions starting from the dragged checkpoint
      // The dragged checkpoint keeps its user-defined start time
      
      // Recalculate checkpoints AFTER the dragged one
      for (let i = newDraggedIndex + 1; i < this.checkpoints.length; i++) {
        const previousCheckpoint = this.checkpoints[i - 1];
        const currentCheckpoint = this.checkpoints[i];
        const duration = this.getDurationInMinutes(currentCheckpoint);
        
        // Start this checkpoint at the end of the previous one
        currentCheckpoint.startDate = previousCheckpoint.endDate;
        currentCheckpoint.endDate = currentCheckpoint.startDate.plus({ minutes: duration });
      }
      
      // Recalculate checkpoints BEFORE the dragged one if there are overlaps
      for (let i = newDraggedIndex - 1; i >= 0; i--) {
        const currentCheckpoint = this.checkpoints[i];
        const nextCheckpoint = this.checkpoints[i + 1];
        
        // If current checkpoint overlaps with next, move it backwards
        if (currentCheckpoint.endDate.toMillis() > nextCheckpoint.startDate.toMillis()) {
          const duration = this.getDurationInMinutes(currentCheckpoint);
          currentCheckpoint.endDate = nextCheckpoint.startDate;
          currentCheckpoint.startDate = currentCheckpoint.endDate.minus({ minutes: duration });
        }
      }
    },
    
    recalculateSchedule() {
      if (this.checkpoints.length <= 1) return;
      
      // Sort checkpoints by start time
      this.checkpoints.sort((a, b) => a.startDate.toMillis() - b.startDate.toMillis());
      
      // Adjust times to ensure continuity
      for (let i = 1; i < this.checkpoints.length; i++) {
        const previousCheckpoint = this.checkpoints[i - 1];
        const currentCheckpoint = this.checkpoints[i];
        
        // If there's a gap or overlap, adjust the current checkpoint
        if (currentCheckpoint.startDate.toMillis() !== previousCheckpoint.endDate.toMillis()) {
          const duration = this.getDurationInMinutes(currentCheckpoint);
          currentCheckpoint.startDate = previousCheckpoint.endDate;
          currentCheckpoint.endDate = currentCheckpoint.startDate.plus({ minutes: duration });
        }
      }
    }
  }
};
</script>

<style scoped>
.k4-timeline-container {
  background: var(--main-container-bg-color);
  border-radius: 8px;
  padding: 20px;
  margin: 10px 0;
  box-shadow: rgba(0, 0, 0, 0.12) 0px 1px 3px, rgba(0, 0, 0, 0.24) 0px 1px 2px;
}

.k4-timeline-header h5 {
  color: var(--main-k4-primary-color);
  font-weight: 600;
}

.k4-timeline-info {
  font-size: 0.9em;
  margin-bottom: 20px;
}

.k4-timeline-scale {
  position: relative;
  height: 30px;
  margin-bottom: 5px;
  display: flex;
  align-items: center;
}

.k4-timeline-scale-label {
  font-size: 0.9em;
  font-weight: 500;
  color: var(--main-k4-primary-color);
  margin-right: 15px;
}

.k4-timeline-content {
  position: relative;
  height: 160px; /* Increased to accommodate integrated scale */
  overflow-x: auto;
  overflow-y: visible;
  border: 1px solid #eee;
  border-radius: 4px;
  background: #fafafa;
}

.k4-timeline-track {
  position: relative;
  height: 100%;
  min-width: 100%;
}

.k4-timeline-hours-integrated {
  position: absolute;
  top: 0;
  left: 0;
  height: 40px;
  width: 100%;
  z-index: 10;
  background: rgba(250, 250, 250, 0.95);
  border-bottom: 2px solid var(--main-k4-primary-color);
}

.k4-hour-marker-integrated {
  position: absolute;
  height: 100%;
  border-left: 1px solid #ddd;
}

.k4-hour-marker-integrated:last-child {
  border-right: 1px solid #ddd;
}

.k4-hour-label-integrated {
  position: absolute;
  top: 5px;
  left: 5px;
  font-size: 0.8em;
  font-weight: 500;
  color: var(--main-k4-primary-color);
  background: rgba(250, 250, 250, 0.9);
  padding: 2px 4px;
  border-radius: 2px;
}

.k4-quarter-markers-integrated {
  position: relative;
  height: 100%;
}

.k4-quarter-marker-integrated {
  position: absolute;
  top: 20px;
  height: 20px;
  width: 1px;
  background-color: #eee;
}

.k4-timeline-track {
  position: relative;
  height: 100%;
  min-width: 100%;
}

.k4-timeline-grid {
  position: absolute;
  top: 40px; /* Start below the integrated scale */
  left: 0;
  height: calc(100% - 40px);
  width: 100%;
  pointer-events: none;
}

.k4-grid-column {
  position: absolute;
  height: 100%;
  border-right: 1px solid rgba(221, 221, 221, 0.3);
}

.k4-grid-column:nth-child(even) {
  background: rgba(35, 82, 164, 0.02);
}

.k4-checkpoint-block {
  position: absolute;
  top: 50px; /* Positioned below the integrated scale */
  height: 80px;
  background: linear-gradient(135deg, var(--main-k4-primary-color), #1a4082);
  color: white;
  border-radius: 6px;
  cursor: move;
  user-select: none;
  box-shadow: rgba(0, 0, 0, 0.2) 0px 2px 4px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  min-width: 60px;
}

.k4-checkpoint-block:hover {
  box-shadow: rgba(0, 0, 0, 0.3) 0px 4px 8px;
  transform: translateY(-1px);
  z-index: 100; /* Passe au-dessus des autres checkpoints */
}

.k4-checkpoint-block.k4-dragging {
  opacity: 0.8;
  z-index: 1000;
  transform: rotate(2deg);
}

.k4-checkpoint-block.k4-resizing {
  box-shadow: rgba(0, 0, 0, 0.4) 0px 4px 12px;
}

.k4-checkpoint-block.k4-invalid {
  background: linear-gradient(135deg, #dc3545, #c82333);
  box-shadow: 0 0 0 2px rgba(220, 53, 69, 0.3);
}

.k4-checkpoint-content {
  text-align: center;
  padding: 8px;
  flex: 1;
}

.k4-checkpoint-name {
  font-weight: 600;
  font-size: 0.9em;
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.k4-checkpoint-direction {
  font-size: 0.75em;
  opacity: 0.9;
  margin-bottom: 4px;
}

.k4-checkpoint-time {
  font-size: 0.75em;
  opacity: 0.9;
  margin-bottom: 2px;
}

.k4-checkpoint-duration {
  font-size: 0.7em;
  opacity: 0.8;
  font-weight: 500;
}

.k4-resize-handle {
  position: absolute;
  top: 0;
  width: 8px;
  height: 100%;
  cursor: ew-resize;
  background: rgba(255, 255, 255, 0.2);
  opacity: 0;
  transition: opacity 0.2s ease;
}

.k4-checkpoint-block:hover .k4-resize-handle {
  opacity: 1;
}

.k4-resize-left {
  left: 0;
  border-radius: 6px 0 0 6px;
}

.k4-resize-right {
  right: 0;
  border-radius: 0 6px 6px 0;
}

.k4-checkpoint-delete {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 20px;
  height: 20px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0;
  transition: all 0.2s ease;
  font-size: 0.8em;
}

.k4-checkpoint-block:hover .k4-checkpoint-delete {
  opacity: 1;
}

.k4-checkpoint-delete:hover {
  background: rgba(220, 53, 69, 0.8);
  transform: scale(1.1);
}

.k4-drag-placeholder {
  position: absolute;
  top: 50px; /* Same as checkpoints */
  height: 80px;
  background: rgba(35, 82, 164, 0.3);
  border: 2px dashed var(--main-k4-primary-color);
  border-radius: 6px;
  pointer-events: none;
}

.k4-add-checkpoint {
  padding: 20px;
  background: rgba(35, 82, 164, 0.05);
  border-radius: 8px;
  border: 1px solid rgba(35, 82, 164, 0.2);
  margin-top: 20px;
}

.k4-add-checkpoint-title {
  color: var(--main-k4-primary-color);
  font-weight: 600;
  margin-bottom: 15px;
  font-size: 1rem;
}

.k4-checkpoint-cards-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 15px;
}

.k4-checkpoint-card {
  background: var(--main-container-bg-color);
  border: 2px solid rgba(35, 82, 164, 0.15);
  border-radius: 8px;
  padding: 15px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: rgba(0, 0, 0, 0.08) 0px 2px 4px;
  position: relative;
  overflow: hidden;
}

.k4-checkpoint-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, var(--main-k4-primary-color), #1a4082);
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.3s ease;
}

.k4-checkpoint-card:hover {
  border-color: var(--main-k4-primary-color);
  box-shadow: rgba(35, 82, 164, 0.2) 0px 4px 12px;
  transform: translateY(-2px);
}

.k4-checkpoint-card:hover::before {
  transform: scaleX(1);
}

.k4-checkpoint-card:active {
  transform: translateY(0);
  box-shadow: rgba(35, 82, 164, 0.3) 0px 2px 8px;
}

.k4-checkpoint-card-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.k4-checkpoint-card-name {
  font-weight: 600;
  color: var(--main-k4-primary-color);
  font-size: 0.95rem;
  flex: 1;
  margin-right: 10px;
  line-height: 1.3;
}

.k4-checkpoint-card-button {
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, var(--main-k4-primary-color), #1a4082);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: rgba(35, 82, 164, 0.3) 0px 2px 6px;
}

.k4-checkpoint-card:hover .k4-checkpoint-card-button {
  transform: scale(1.1);
  box-shadow: rgba(35, 82, 164, 0.4) 0px 4px 8px;
}

.k4-checkpoint-card-button svg {
  font-size: 0.9rem;
}

.k4-validation-errors {
  margin-top: 15px;
}

.k4-validation-errors .alert {
  background: rgba(255, 193, 7, 0.1);
  border-color: rgba(255, 193, 7, 0.3);
  color: #856404;
}

.k4-resize-tooltip {
  position: fixed;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.8em;
  pointer-events: none;
  z-index: 10000;
}

.k4-checkpoint-tooltip {
  position: fixed;
  background: rgba(0, 0, 0, 0.9);
  color: white;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 0.85em;
  pointer-events: none;
  z-index: 10000;
  box-shadow: rgba(0, 0, 0, 0.3) 0px 4px 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.k4-tooltip-name {
  font-weight: 600;
  font-size: 0.9em;
  margin-bottom: 2px;
  color: #fff;
}

.k4-tooltip-direction {
  font-size: 0.8em;
  color: #ccc;
  margin-bottom: 2px;
}

.k4-tooltip-time {
  font-size: 0.8em;
  color: #ddd;
}

/* Modal styles */
.modal input[readonly], .modal input:disabled {
  background-color: #f8f9fa;
  color: #6c757d;
  cursor: not-allowed;
  border-color: #dee2e6;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .k4-timeline-container {
    padding: 15px;
  }
  
  .k4-checkpoint-content {
    padding: 4px;
  }
  
  .k4-checkpoint-name {
    font-size: 0.8em;
  }
  
  .k4-checkpoint-direction,
  .k4-checkpoint-time {
    font-size: 0.7em;
  }
  
  .k4-checkpoint-duration {
    font-size: 0.65em;
  }
  
  .k4-timeline-content {
    height: 140px; /* Adjusted for mobile */
  }
  
  .k4-checkpoint-block {
    height: 60px;
    top: 45px; /* Adjusted for mobile */
  }
  
  .k4-drag-placeholder {
    height: 60px;
    top: 45px; /* Adjusted for mobile */
  }
  
  .k4-hour-label-integrated {
    font-size: 0.7em;
  }
  
  .k4-checkpoint-cards-container {
    grid-template-columns: 1fr;
    gap: 10px;
  }
  
  .k4-checkpoint-card {
    padding: 12px;
  }
  
  .k4-checkpoint-card-name {
    font-size: 0.9rem;
  }
  
  .k4-checkpoint-card-button {
    width: 28px;
    height: 28px;
  }
}

@media (max-width: 480px) {
  .k4-timeline-container {
    padding: 10px;
  }
  
  .k4-hour-label-integrated {
    font-size: 0.65em;
    padding: 1px 2px;
  }
  
  .k4-checkpoint-content {
    padding: 2px;
  }
  
  .k4-checkpoint-name {
    font-size: 0.75em;
    margin-bottom: 1px;
  }
  
  .k4-checkpoint-direction,
  .k4-checkpoint-time {
    font-size: 0.65em;
  }
  
  .k4-checkpoint-duration {
    font-size: 0.6em;
  }
  
  .k4-timeline-content {
    height: 120px; /* Further reduced for small screens */
  }
  
  .k4-checkpoint-block {
    height: 50px;
    top: 40px; /* Adjusted for small screens */
  }
  
  .k4-drag-placeholder {
    height: 50px;
    top: 40px; /* Adjusted for small screens */
  }
  
  .k4-add-checkpoint {
    padding: 15px;
  }
  
  .k4-checkpoint-cards-container {
    gap: 8px;
  }
  
  .k4-checkpoint-card {
    padding: 10px;
  }
  
  .k4-checkpoint-card-name {
    font-size: 0.85rem;
  }
  
  .k4-checkpoint-card-button {
    width: 26px;
    height: 26px;
  }
  
  .k4-checkpoint-card-button svg {
    font-size: 0.8rem;
  }
}

/* Animations */
.k4-checkpoint-block {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.k4-checkpoint-block:hover {
  transform: translateY(-2px) scale(1.02);
  z-index: 100; /* Passe au-dessus des autres checkpoints */
}

.k4-checkpoint-block.k4-dragging {
  animation: dragPulse 1s infinite;
  z-index: 1000;
}

@keyframes dragPulse {
  0%, 100% { 
    box-shadow: rgba(35, 82, 164, 0.4) 0px 4px 12px; 
  }
  50% { 
    box-shadow: rgba(35, 82, 164, 0.8) 0px 8px 24px; 
  }
}

.k4-drag-placeholder {
  animation: placeholderPulse 1.5s infinite;
}

@keyframes placeholderPulse {
  0%, 100% { 
    opacity: 0.3; 
  }
  50% { 
    opacity: 0.6; 
  }
}

/* Better focus states for accessibility */
.k4-checkpoint-block:focus {
  outline: 2px solid var(--main-k4-primary-color);
  outline-offset: 2px;
}

/* Smooth transitions for tooltips */
.k4-resize-tooltip {
  animation: tooltipFadeIn 0.2s ease-out;
}

@keyframes tooltipFadeIn {
  from {
    opacity: 0;
    transform: translateY(-5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Enhanced visual feedback for interactive elements */
.k4-checkpoint-delete {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.k4-resize-handle {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.k4-resize-handle:hover {
  background: rgba(255, 255, 255, 0.4);
  backdrop-filter: blur(2px);
}

/* Timeline grid enhancements */
.k4-hour-marker:nth-child(even) {
  background: rgba(35, 82, 164, 0.02);
}

.k4-quarter-marker {
  transition: opacity 0.2s ease;
}

.k4-timeline-content:hover .k4-quarter-marker {
  opacity: 0.8;
}

.k4-checkpoint-tooltip {
  position: fixed;
  background: rgba(0, 0, 0, 0.9);
  color: white;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 0.85em;
  pointer-events: none;
  z-index: 10000;
  box-shadow: rgba(0, 0, 0, 0.3) 0px 4px 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.k4-tooltip-name {
  font-weight: 600;
  font-size: 0.9em;
  margin-bottom: 2px;
  color: #fff;
}

.k4-tooltip-direction {
  font-size: 0.8em;
  color: #ccc;
  margin-bottom: 2px;
}

.k4-tooltip-time {
  font-size: 0.8em;
  color: #ddd;
}

/* Loading overlay */
.k4-timeline-loading {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.95);
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  z-index: 10;
}

.k4-timeline-loading p {
  margin: 0;
  color: #666;
  font-size: 0.9rem;
}
</style>

